// Field Notes from St. Kitts & Nevis — editorial seed data.
// Static for now; can later be moved to Supabase if a CMS workflow is needed.

import frigateBay from "@/assets/discover/frigate-bay-strip.jpg";
import churchBySea from "@/assets/heritage/church-by-sea.jpg";
import nevisPeak from "@/assets/discover/nevis-peak-from-water.jpg";
import goldenRock from "@/assets/heritage/golden-rock.jpg";
import nevisMontpelier from "@/assets/discover/nevis-montpelier.jpg";
import basseterreHarbor from "@/assets/discover/basseterre-harbor.jpg";
import peninsulaTwoSeas from "@/assets/discover/peninsula-two-seas.jpg";
import mountainPath from "@/assets/heritage/mountain-path.jpg";
import rumShop from "@/assets/heritage/rum-shop.jpg";
import rainforestCanopy from "@/assets/discover/rainforest-canopy.jpg";
import islandPanorama from "@/assets/discover/island-panorama-hero.jpg";
import nevisScenic from "@/assets/discover/nevis-scenic.jpg";
import nevisArtOfSlowing from "@/assets/discover/nevis-art-of-slowing.jpg";

export type IslandTone = "st-kitts" | "nevis" | "both";

export type FieldNoteTag =
  | "Slow Travel"
  | "Island Rhythm"
  | "Wellness"
  | "Villages"
  | "Heritage"
  | "Nature"
  | "Emotional Geography"
  | "Food & Rum"
  | "Ferry Life"
  | "Quiet Places";

export const ALL_TAGS: FieldNoteTag[] = [
  "Slow Travel",
  "Island Rhythm",
  "Wellness",
  "Villages",
  "Heritage",
  "Nature",
  "Emotional Geography",
  "Food & Rum",
  "Ferry Life",
  "Quiet Places",
];

export interface PullQuote {
  text: string;
  attribution?: string;
}

export interface FieldNoteSection {
  heading?: string;
  paragraphs: string[];
  pullQuote?: PullQuote;
}

export interface FieldNote {
  slug: string;
  title: string;
  dek: string;
  island: IslandTone;
  tags: FieldNoteTag[];
  heroImage: string;
  excerpt: string;
  sections: FieldNoteSection[];
  softCta: { label: string; href: string; note: string };
  /** Optional Shopify product handles to surface as a Ritual Companion inside the essay. */
  companionHandles?: string[];
}

export const FIELD_NOTES: FieldNote[] = [
  {
    slug: "the-rhythm-of-the-islands",
    title: "The Rhythm of the Islands",
    dek: "How a place teaches you a different relationship with time.",
    island: "both",
    tags: ["Island Rhythm", "Slow Travel", "Emotional Geography"],
    heroImage: frigateBay,
    excerpt:
      "The islands keep their own time. It is not late and it is not early. It is its own measure, set by light, breeze and the small ceremonies of village life.",
    sections: [
      {
        paragraphs: [
          "There is a tempo the islands keep that you only notice once you have surrendered to it. It is slower than you think, and more exact. A morning here is not measured in minutes but in the order of small ceremonies: a kettle on, a gate unlatched, a neighbour passing.",
          "By mid-morning the heat softens conversation. By the time the rain comes in the afternoon, even the dogs have arranged themselves into a kind of stillness. Nothing is rushed because nothing rewards rushing.",
        ],
      },
      {
        heading: "The afternoons that taught me to wait",
        paragraphs: [
          "If you have always lived inside a calendar, the first few days feel like a malfunction. Then the rhythm slips under your skin. You begin walking slower without realising it. You stop reaching for your phone. You let a conversation finish itself.",
        ],
        pullQuote: {
          text: "The islands don't make you slower. They reveal how much speed you were carrying.",
        },
      },
    ],
    softCta: {
      label: "Step into the Caribbean Longevity Method",
      href: "/caribbean-heritage-longevity-method",
      note: "A slower way of living, rooted in island rhythm.",
    },
    companionHandles: ["14-day-reset"],
  },
  {
    slug: "why-sundays-feel-different-in-st-kitts",
    title: "Why Sundays Feel Different in St. Kitts",
    dek: "Church bells, slow lunches and the village shutter-down.",
    island: "st-kitts",
    tags: ["Island Rhythm", "Villages", "Heritage", "Quiet Places"],
    heroImage: churchBySea,
    excerpt:
      "Sundays in St. Kitts do not behave like other days. Even the breeze seems to honour them. The villages exhale.",
    sections: [
      {
        paragraphs: [
          "Sundays in St. Kitts arrive like a held breath. Shops close. Engines quiet. The first sound you hear is a church bell, and the last sound you hear, hours later, is the same bell answered by another, somewhere further down the coast.",
          "Lunch becomes a long event. Aunties cook. Cousins visit. Children loop the yard. Time bends gently around the table.",
        ],
        pullQuote: {
          text: "A St. Kitts Sunday is a small national act of slowing down.",
        },
      },
      {
        heading: "What the quiet asks of you",
        paragraphs: [
          "You do not need to be religious to feel it. The quiet is communal. It is older than any one of us. By late afternoon, the light goes copper and the day folds itself away like a clean linen.",
        ],
      },
    ],
    softCta: {
      label: "Reserve a Sunrise & Stillness morning",
      href: "/experiences/sunrise-and-stillness",
      note: "A quieter way to meet the island.",
    },
    companionHandles: ["self-love-journal"],
  },
  {
    slug: "the-emotional-difference-between-st-kitts-and-nevis",
    title: "The Emotional Difference Between St. Kitts & Nevis",
    dek: "Two islands, two interior temperatures.",
    island: "both",
    tags: ["Emotional Geography", "Slow Travel", "Heritage"],
    heroImage: nevisPeak,
    excerpt:
      "St. Kitts is layered, social, rhythm-driven. Nevis is quieter — almost suspended. You feel the difference before you can name it.",
    sections: [
      {
        paragraphs: [
          "St. Kitts arrives in layers. There is the harbour, the market voices, the music drifting from a passing car, the heat of conversation outside a rum shop. The island reveals itself the way a long meal does — course by course, with company.",
          "Nevis is different. Nevis is one long exhale. The roads are softer. The trees are taller. The light feels held. You arrive and a part of you that has been bracing for a year quietly puts itself down.",
        ],
        pullQuote: {
          text: "St. Kitts is alive in the room with you. Nevis is the quiet after the door closes.",
        },
      },
      {
        heading: "Both are necessary",
        paragraphs: [
          "Visitors often choose. Locals know better. The two islands belong to each other. One gives you texture; the other gives you space. Together, they teach a fuller kind of rest.",
        ],
      },
    ],
    softCta: {
      label: "Wander the Discover archive",
      href: "/discover-st-kitts",
      note: "More observations from across the federation.",
    },
  },
  {
    slug: "the-romance-st-kitts-hides",
    title: "The Romance St. Kitts Hides",
    dek: "A small island with a long memory and an even longer evening.",
    island: "st-kitts",
    tags: ["Emotional Geography", "Heritage", "Quiet Places"],
    heroImage: goldenRock,
    excerpt:
      "St. Kitts does not perform romance. It allows it. The romance is in the in-between hours, the half-light, the unposted views.",
    sections: [
      {
        paragraphs: [
          "St. Kitts is not a postcard island that markets itself with sunsets and champagne. Its romance is quieter and stranger. It lives in an old stone wall along a back road. In the unexpected garden behind a plantation ruin. In the long blue hour before the sun gives up.",
        ],
      },
      {
        heading: "Where to find it",
        paragraphs: [
          "Drive the coast at five o'clock with the windows down. Park near a fishing village. Walk into a small church courtyard. Order a drink at a bar where no one is performing for anyone. The island will meet you there.",
        ],
        pullQuote: {
          text: "Romance here is not a stage. It is a habit of the light.",
        },
      },
    ],
    softCta: {
      label: "Explore a botanical walk",
      href: "/experiences/sunrise-and-stillness",
      note: "Soft mornings, old paths, kept secrets.",
    },
  },
  {
    slug: "nevis-and-the-art-of-slowing-down",
    title: "Nevis and the Art of Slowing Down",
    dek: "A field guide to doing very little, beautifully.",
    island: "nevis",
    tags: ["Slow Travel", "Wellness", "Quiet Places"],
    heroImage: nevisArtOfSlowing,
    excerpt:
      "Nevis does not entertain you. It returns you to yourself. The island's whole genius is restraint.",
    sections: [
      {
        paragraphs: [
          "On Nevis, doing nothing becomes a discipline. You sit on a veranda. You watch the goats. You watch the mountain decide whether to wear its cloud. You read a book the way a book is meant to be read — without finishing it in one sitting.",
          "The island rewards patience. The hot springs at Bath. The slow loop around the coast. The quiet beaches with no one selling anything. You stop measuring days and start measuring afternoons.",
        ],
        pullQuote: {
          text: "Nevis is not a destination. It is a temperature.",
        },
      },
    ],
    softCta: {
      label: "Plan a Sunrise & Stillness retreat",
      href: "/experiences/sunrise-and-stillness",
      note: "Built for the kind of rest Nevis teaches.",
    },
  },
  {
    slug: "the-quiet-after-cruise-ships-leave",
    title: "The Quiet After Cruise Ships Leave",
    dek: "What Basseterre becomes once the harbour empties.",
    island: "st-kitts",
    tags: ["Island Rhythm", "Quiet Places", "Heritage"],
    heroImage: basseterreHarbor,
    excerpt:
      "There is a particular silence after the last cruise ship pulls away. It is the island remembering itself.",
    sections: [
      {
        paragraphs: [
          "Basseterre in cruise hours wears a borrowed face. Vendors call. Taxis line up. The Circus crowds. It is alive, but it is performing.",
          "Then the ships leave. The horizon empties. The streets exhale. Locals reappear at their usual benches. The Circus becomes a clock again, not a backdrop. The island returns to itself.",
        ],
        pullQuote: {
          text: "Stay long enough to meet the town when the cameras have gone.",
        },
      },
    ],
    softCta: {
      label: "Read more from the Discover archive",
      href: "/discover-st-kitts",
      note: "Quiet routes, slow afternoons, real places.",
    },
  },
  {
    slug: "the-atlantic-side-vs-the-caribbean-side",
    title: "The Atlantic Side vs the Caribbean Side",
    dek: "One island, two oceans, two moods.",
    island: "st-kitts",
    tags: ["Nature", "Emotional Geography", "Slow Travel"],
    heroImage: peninsulaTwoSeas,
    excerpt:
      "Stand on the peninsula at the right hour and you can feel two oceans behaving like two different people in the same room.",
    sections: [
      {
        paragraphs: [
          "The Caribbean side is calm. Glass-flat in the morning. Easy to enter. It invites you in like a host.",
          "The Atlantic side does not. It is louder, older, more honest. It does not arrange itself for visitors. The wind has things to say. The water keeps moving.",
        ],
        pullQuote: {
          text: "One side soothes you. The other side reminds you who you are.",
        },
      },
      {
        heading: "Why both matter",
        paragraphs: [
          "A complete day on St. Kitts touches both. The Caribbean coast for the morning swim. The Atlantic for the late-afternoon walk that puts the day in perspective.",
        ],
      },
    ],
    softCta: {
      label: "Discover the Nature of St. Kitts",
      href: "/nature-of-st-kitts",
      note: "Coastlines, ridges and quiet wild places.",
    },
  },
  {
    slug: "why-time-feels-different-here",
    title: "Why Time Feels Different Here",
    dek: "On island clocks, slow weather and the long afternoon.",
    island: "both",
    tags: ["Slow Travel", "Wellness", "Emotional Geography"],
    heroImage: mountainPath,
    excerpt:
      "Time on the islands does not pass. It widens. The afternoon stretches the way warm metal does in the sun.",
    sections: [
      {
        paragraphs: [
          "On the islands, time is held in light, not numbers. You learn the day by the angle of the sun on a wall. You learn the week by which boats are in the harbour. You stop being early. You stop being late. You simply arrive.",
        ],
      },
      {
        heading: "The longevity in it",
        paragraphs: [
          "There is a reason elders here live long. The day has air in it. Meals have hours around them. Rest is not earned, it is woven in. Modern wellness has only recently begun to catch up with what these islands have always known.",
        ],
        pullQuote: {
          text: "Time here does not move forward so much as deepen.",
        },
      },
    ],
    softCta: {
      label: "Read the Caribbean Heritage Longevity Method",
      href: "/caribbean-heritage-longevity-method",
      note: "Where this slowness becomes a practice.",
    },
  },
  {
    slug: "village-roads-rum-shops-and-afternoon-rain",
    title: "Village Roads, Rum Shops & Afternoon Rain",
    dek: "A small love letter to the in-between hours.",
    island: "st-kitts",
    tags: ["Villages", "Food & Rum", "Island Rhythm"],
    heroImage: rumShop,
    excerpt:
      "The truest hours on the island are the in-between ones — a wet road, a rum shop bench, the slow gossip of older men, a sky deciding what to do.",
    sections: [
      {
        paragraphs: [
          "Drive through any village in the late afternoon and you will find a small wooden shop with a hand-painted sign. Inside, there are usually two or three older men, a fan, a bottle, and a conversation that started long before you arrived and will continue long after you leave.",
          "When the rain comes, no one moves. The rain is not an interruption. It is a guest. It is poured a drink.",
        ],
        pullQuote: {
          text: "Rum shops are not bars. They are the village living room.",
        },
      },
    ],
    softCta: {
      label: "Visit Di Organics Shoppe",
      href: "/visit-di-organics-shoppe",
      note: "Our small village storefront in St. Kitts.",
    },
  },
  {
    slug: "the-sound-of-tree-frogs-at-night",
    title: "The Sound of Tree Frogs at Night",
    dek: "The island's nightly orchestra and what it does to a tired mind.",
    island: "both",
    tags: ["Nature", "Quiet Places", "Wellness"],
    heroImage: rainforestCanopy,
    excerpt:
      "At dusk the rainforest begins to speak. By full dark, the tree frogs are conducting an orchestra older than any of us.",
    sections: [
      {
        paragraphs: [
          "City visitors notice it on their first night. The constant, gentle chorus of small frogs in the trees. It is not noise. It is texture. A soft static of life that the island plays every evening, free of charge.",
          "After a few nights you stop hearing it consciously. After a few more, you cannot fall asleep without it.",
        ],
        pullQuote: {
          text: "The frogs are not a sound. They are the island's heartbeat made audible.",
        },
      },
    ],
    softCta: {
      label: "Walk a botanical trail at dusk",
      href: "/experiences/sunrise-and-stillness",
      note: "A guided way into the quiet hour.",
    },
  },
];

export const ARCHIVE_HERO_IMAGE = islandPanorama;
export const OBSERVATION_BG_IMAGE = nevisScenic;

export const getFieldNoteBySlug = (slug: string): FieldNote | undefined =>
  FIELD_NOTES.find((n) => n.slug === slug);

export const getRelatedFieldNotes = (
  slug: string,
  count = 2,
): FieldNote[] => {
  const current = getFieldNoteBySlug(slug);
  if (!current) return FIELD_NOTES.slice(0, count);
  const scored = FIELD_NOTES.filter((n) => n.slug !== slug).map((n) => {
    const tagOverlap = n.tags.filter((t) => current.tags.includes(t)).length;
    const islandMatch = n.island === current.island ? 1 : 0;
    return { note: n, score: tagOverlap * 2 + islandMatch };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.note);
};

export const islandLabel = (island: IslandTone): string =>
  island === "st-kitts"
    ? "St. Kitts"
    : island === "nevis"
      ? "Nevis"
      : "St. Kitts & Nevis";
